import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  ChevronLeft, Save, User, Award, Users, Shield, Mail, Phone, ExternalLink, Sparkles, ClipboardList, Globe,
} from 'lucide-react';
import supabase from '../lib/supabase';
import PhotoUploader from '../components/PhotoUploader';
import {
  PageHeader, Section, Badge, Button, Field, Input, Textarea, Select, Checkbox, StatusLine,
} from '../components/ui';

type Title =
  | 'tennis_coach' | 'head_coach' | 'junior_development_coach'
  | 'assistant_coach' | 'performance_coach' | 'other';
type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';
type OppPolicy = 'available' | 'not_available' | 'upon_request';
type Audience = 'men' | 'women' | 'boys' | 'girls' | 'mixed_groups' | 'families';
type Program = 'private_lessons' | 'group_classes' | 'kids_classes' | 'ladies_classes' | 'performance_training';

type Coach = {
  id: string;
  display_name: string;
  bio: string | null;
  photo_url: string | null;
  years_experience: number | null;
  specialties: string[];
  languages: string[];
  title: Title | null;
  title_other: string | null;
  gender: Gender | null;
  nationality: string | null;
  available_to_coach: Audience[];
  opposite_gender_policy: OppPolicy | null;
  opposite_gender_policy_note: string | null;
  qualifications: string[];
  programs: Program[];
  contact_email: string | null;
  contact_phone: string | null;
  booking_link: string | null;
  verification_status: string;
};

const TITLE_LABELS: Record<Title, string> = {
  tennis_coach: 'Tennis Coach',
  head_coach: 'Head Coach',
  junior_development_coach: 'Junior Development Coach',
  assistant_coach: 'Assistant Coach',
  performance_coach: 'Performance Coach',
  other: 'Other\u2026',
};
const GENDER_LABELS: Record<Gender, string> = {
  male: 'Male', female: 'Female', other: 'Other', prefer_not_to_say: 'Prefer not to say',
};
const AUDIENCE_LABELS: Record<Audience, string> = {
  men: 'Men', women: 'Women', boys: 'Boys', girls: 'Girls',
  mixed_groups: 'Mixed groups', families: 'Families',
};
const PROGRAM_LABELS: Record<Program, string> = {
  private_lessons: 'Private lessons',
  group_classes: 'Group classes',
  kids_classes: 'Kids classes',
  ladies_classes: 'Ladies classes',
  performance_training: 'Performance training',
};
const OPP_POLICY_LABELS: Record<OppPolicy, string> = {
  available: 'Available',
  not_available: 'Not available',
  upon_request: 'Available upon request',
};

const LANGUAGE_OPTIONS = [
  'English', 'Arabic', 'Hindi', 'Urdu', 'Persian (Farsi)', 'Russian',
  'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Dutch',
  'Tagalog', 'Tamil', 'Malayalam', 'Bengali',
  'Turkish', 'Mandarin Chinese', 'Japanese', 'Korean',
] as const;

/**
 * Super-admin edit form for the extended coach profile. Mirrors the
 * partner CoachProfile surface so both manager flows produce the same
 * shape. RLS on coaches ("Admin manages coaches") enforces the authZ
 * gate; this page relies on that — no extra client-side check.
 */
export default function CoachEdit() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [coach, setCoach] = useState<Coach | null>(null);

  const [photoUrl, setPhotoUrl] = useState('');
  const [title, setTitle] = useState<Title | ''>('');
  const [titleOther, setTitleOther] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [nationality, setNationality] = useState('');
  const [bio, setBio] = useState('');
  const [years, setYears] = useState('');
  const [specialtiesText, setSpecialtiesText] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [customLanguage, setCustomLanguage] = useState('');
  const [qualificationsText, setQualificationsText] = useState('');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [audience, setAudience] = useState<Audience[]>([]);
  const [oppPolicy, setOppPolicy] = useState<OppPolicy | ''>('');
  const [oppPolicyNote, setOppPolicyNote] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [bookingLink, setBookingLink] = useState('');

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await supabase.from('coaches').select('*').eq('id', id).single();
      const c = data as Coach;
      setCoach(c);
      setPhotoUrl(c.photo_url ?? '');
      setTitle((c.title as Title) ?? '');
      setTitleOther(c.title_other ?? '');
      setGender((c.gender as Gender) ?? '');
      setNationality(c.nationality ?? '');
      setBio(c.bio ?? '');
      setYears(c.years_experience != null ? String(c.years_experience) : '');
      setSpecialtiesText((c.specialties ?? []).join(', '));
      setLanguages(c.languages ?? []);
      setQualificationsText((c.qualifications ?? []).join('\n'));
      setPrograms((c.programs ?? []) as Program[]);
      setAudience((c.available_to_coach ?? []) as Audience[]);
      setOppPolicy((c.opposite_gender_policy as OppPolicy) ?? '');
      setOppPolicyNote(c.opposite_gender_policy_note ?? '');
      setContactEmail(c.contact_email ?? '');
      setContactPhone(c.contact_phone ?? '');
      setBookingLink(c.booking_link ?? '');
    })();
  }, [id]);

  const toggleInArray = <T extends string>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.from('coaches').update({
      photo_url: photoUrl || null,
      title: title || null,
      title_other: title === 'other' ? (titleOther.trim() || null) : null,
      gender: gender || null,
      nationality: nationality.trim() || null,
      bio: bio || null,
      years_experience: years ? Number(years) : null,
      specialties: splitCsv(specialtiesText),
      languages,
      qualifications: splitLines(qualificationsText),
      programs,
      available_to_coach: audience,
      opposite_gender_policy: oppPolicy || null,
      opposite_gender_policy_note: oppPolicyNote.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      booking_link: bookingLink.trim() || null,
    }).eq('id', id).select('id');
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    if (!data || data.length === 0) {
      setMsg({ kind: 'error', text: "Nothing was saved — RLS rejected the write." });
      return;
    }
    setMsg({ kind: 'success', text: 'Profile saved.' });
  };

  if (!coach) return <div className="p-8 text-sm text-gray-500">Loading\u2026</div>;

  return (
    <div className="p-8 max-w-4xl">
      <Link to={`/coaches/${id}`}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
        <ChevronLeft className="w-4 h-4" /> Back to coach
      </Link>

      <PageHeader
        eyebrow="Edit coach"
        title={coach.display_name}
        subtitle={(
          <span className="flex items-center gap-2 flex-wrap">
            <Badge tone={
              coach.verification_status === 'verified' ? 'green'
              : coach.verification_status === 'rejected' ? 'red' : 'amber'
            }>
              {coach.verification_status.replace('_', ' ')}
            </Badge>
          </span>
        )}
      />

      <form onSubmit={save} className="space-y-0">
        <Section title="Identity" subtitle="Photo, title, and how the coach is presented on the customer app.">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-24 h-24 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
              {photoUrl
                ? <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                : <User className="w-8 h-8 text-gray-400" />}
            </div>
            <div className="flex-1">
              <div className="text-xs font-medium text-gray-700 mb-1">Profile photo</div>
              <div className="text-[11px] text-gray-500 mb-2">
                JPEG / PNG / WebP, up to 10&nbsp;MB. Square images look best. Saved immediately.
              </div>
              {id && (
                <PhotoUploader
                  bucket="coach-photos"
                  pathPrefix={id}
                  photos={photoUrl ? [photoUrl] : []}
                  onChange={async (urls) => {
                    const next = urls[0] ?? '';
                    setPhotoUrl(next);
                    await supabase.from('coaches')
                      .update({ photo_url: next || null })
                      .eq('id', id);
                  }}
                  max={1}
                  compact
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Title / role">
              <Select value={title} onChange={(e) => setTitle(e.target.value as Title | '')}>
                <option value="">— Select a title —</option>
                {(Object.keys(TITLE_LABELS) as Title[]).map((t) => (
                  <option key={t} value={t}>{TITLE_LABELS[t]}</option>
                ))}
              </Select>
            </Field>
            {title === 'other' && (
              <Field label="Custom title">
                <Input value={titleOther} onChange={(e) => setTitleOther(e.target.value)}
                  placeholder="e.g. Touring professional" />
              </Field>
            )}
            <Field label="Gender">
              <Select value={gender} onChange={(e) => setGender(e.target.value as Gender | '')}>
                <option value="">— Select —</option>
                {(Object.keys(GENDER_LABELS) as Gender[]).map((g) => (
                  <option key={g} value={g}>{GENDER_LABELS[g]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Nationality" hint="Country shown on the coach profile card.">
              <Input value={nationality} onChange={(e) => setNationality(e.target.value)}
                placeholder="UAE, Spain, India, …" />
            </Field>
          </div>
        </Section>

        <Section title="Bio & experience"
          subtitle="The coach's story — background, style, what they love about the game.">
          <div className="space-y-4">
            <Field label="Short bio" hint={`${bio.length}/500`}>
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={500}
                placeholder="Former national-level junior. Seven years coaching adults and juniors." />
            </Field>
            <Field label="Years of experience">
              <Input type="number" min="0" value={years} onChange={(e) => setYears(e.target.value)} className="max-w-xs" />
            </Field>
          </div>
        </Section>

        <Section
          title={<span className="flex items-center gap-2"><Award className="w-4 h-4 text-[#2d5016]" /> Qualifications & specialties</span>}
          subtitle="Badges and focus areas shown on the profile."
        >
          <div className="grid grid-cols-1 gap-3">
            <Field label="Qualifications" hint="One per line. Federation memberships, certifications, licenses.">
              <Textarea rows={4} value={qualificationsText}
                onChange={(e) => setQualificationsText(e.target.value)}
                placeholder={'LTA Level 3\nITF Coach Licence\nUAE Tennis Federation — registered coach'} />
            </Field>
            <Field label="Specialties" hint="Comma-separated tags shown as chips.">
              <Input value={specialtiesText} onChange={(e) => setSpecialtiesText(e.target.value)}
                placeholder="beginner, tactics, serve" />
            </Field>

            <div>
              <div className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Globe className="w-3.5 h-3.5" /> Languages spoken
              </div>
              <div className="text-[11px] text-gray-500 mb-2">
                Pick any languages the coach can teach in. Anything not in the list can be added below.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {LANGUAGE_OPTIONS.map((l) => (
                  <Checkbox key={l}
                    checked={languages.includes(l)}
                    onChange={() => setLanguages(toggleInArray(languages, l))}
                    label={l} />
                ))}
              </div>
              {languages.filter((l) => !LANGUAGE_OPTIONS.includes(l as any)).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {languages.filter((l) => !LANGUAGE_OPTIONS.includes(l as any)).map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2d5016]/10 text-[#1a3010] text-xs">
                      {l}
                      <button type="button" className="text-[#1a3010]/60 hover:text-[#1a3010]"
                        onClick={() => setLanguages(languages.filter((x) => x !== l))}
                        aria-label={`Remove ${l}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <Input value={customLanguage}
                  onChange={(e) => setCustomLanguage(e.target.value)}
                  placeholder="Add another language" className="max-w-xs" />
                <Button type="button" variant="secondary"
                  disabled={!customLanguage.trim()}
                  onClick={() => {
                    const v = customLanguage.trim();
                    if (v && !languages.includes(v)) setLanguages([...languages, v]);
                    setCustomLanguage('');
                  }}>Add</Button>
              </div>
            </div>
          </div>
        </Section>

        <Section
          title={<span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#2d5016]" /> Who they coach</span>}
          subtitle="Helps customers filter for the right fit."
        >
          <div className="space-y-4">
            <div>
              <div className="text-xs font-medium text-gray-700 mb-2">Available to coach</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(Object.keys(AUDIENCE_LABELS) as Audience[]).map((a) => (
                  <Checkbox key={a}
                    checked={audience.includes(a)}
                    onChange={() => setAudience(toggleInArray(audience, a))}
                    label={AUDIENCE_LABELS[a]} />
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" /> Opposite-gender coaching policy
              </div>
              <div className="text-[11px] text-gray-500 mb-2">
                How the coach handles requests from students of the opposite gender. Shown on their customer profile.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {(Object.keys(OPP_POLICY_LABELS) as OppPolicy[]).map((p) => (
                  <label key={p}
                    className={`flex items-start gap-2 rounded-md border p-2 text-xs cursor-pointer ${oppPolicy === p ? 'border-[#2d5016] bg-white' : 'border-gray-200 bg-white/40'}`}>
                    <input type="radio" name="oppPolicy"
                      checked={oppPolicy === p}
                      onChange={() => setOppPolicy(p)} className="mt-0.5" />
                    <span className="font-medium text-gray-900">{OPP_POLICY_LABELS[p]}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <Field label="Policy note (optional)"
                  hint='e.g. "Available for boys under 12 and family sessions."'>
                  <Input value={oppPolicyNote} onChange={(e) => setOppPolicyNote(e.target.value)} />
                </Field>
              </div>
            </div>
          </div>
        </Section>

        <Section
          title={<span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#2d5016]" /> Programs available</span>}
          subtitle="High-level offerings. The rate card (in the partner app) defines prices; this is just what categories of coaching they provide."
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(Object.keys(PROGRAM_LABELS) as Program[]).map((p) => (
              <Checkbox key={p}
                checked={programs.includes(p)}
                onChange={() => setPrograms(toggleInArray(programs, p))}
                label={PROGRAM_LABELS[p]} />
            ))}
          </div>
        </Section>

        <Section
          title={<span className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#2d5016]" /> Contact & booking</span>}
          subtitle="Optional. Empty fields are hidden from customers."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={<span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Contact email</span>}>
              <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </Field>
            <Field label={<span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Contact phone</span>}>
              <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </Field>
            <Field className="sm:col-span-2"
              label={<span className="flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5" /> External booking link</span>}
              hint="Calendly or similar. Shown as a secondary CTA on the customer profile.">
              <Input type="url" value={bookingLink} onChange={(e) => setBookingLink(e.target.value)}
                placeholder="https://calendly.com/…" />
            </Field>
          </div>
        </Section>

        {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

        <div className="sticky bottom-0 bg-[#f6f7f4]/95 backdrop-blur py-3 border-t border-gray-200 flex gap-2">
          <Button type="button" variant="secondary" onClick={() => nav(`/coaches/${id}`)}>Cancel</Button>
          <Button type="submit" loading={busy} icon={Save}>Save profile</Button>
        </div>
      </form>
    </div>
  );
}

function splitCsv(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
function splitLines(s: string): string[] {
  return s.split('\n').map((x) => x.trim()).filter(Boolean);
}
